import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SectionLabel, Reveal, KineticLines } from "../Reveal";

const IMG_MAIN =
  "https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=1200";
const IMG_SECONDARY =
  "https://images.unsplash.com/photo-1764711274301-dcd9c97db41c?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=800";

const MILESTONES = [
  { year: "2001", title: "Started helping businesses grow", copy: "Our story began in 2001, helping businesses attract customers and grow sales." },
  { year: "2014", title: "Building relationships as Community Promotions", copy: "Since 2014, we've continued that mission through Community Promotions, working alongside owners and decision-makers." },
  { year: "Today", title: "Bottom Line Improvements launches", copy: "Created to share the valuable resources and opportunities we've discovered — helping owners strengthen profitability." },
];

export default function HistorySection() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [30, -30]);
  const ySecond = useTransform(scrollYProgress, [0, 1], [50, -50]);

  return (
    <section ref={ref} className="relative py-16 md:py-24 bg-offwhite overflow-hidden" data-testid="history-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <SectionLabel>Our Story</SectionLabel>
            <KineticLines
              as="h2"
              className="mt-5 font-display font-extrabold tracking-tightest text-navy text-3xl sm:text-4xl lg:text-[2.9rem] leading-[1.08]"
              lines={["Built on Relationships.", "Backed by Experience."]}
            />
            <Reveal delay={0.1}>
              <p className="mt-5 text-2xl font-display font-bold text-royal tracking-tight">
                25+ Years Helping Businesses Succeed
              </p>
              <div className="mt-6 space-y-4 text-base md:text-lg leading-relaxed text-slate">
                <p>
                  Our story began in 2001, helping businesses attract customers and grow sales. Since 2014, we've
                  continued that mission through Community Promotions.
                </p>
                <p>
                  Along the way, we've built relationships with business owners who understand that every dollar
                  matters. Bottom Line Improvements was created to share the valuable resources and opportunities
                  we've discovered.
                </p>
              </div>
            </Reveal>

            <div className="mt-10 space-y-0">
              {MILESTONES.map((m, i) => (
                <Reveal key={m.year} delay={i * 0.08}>
                  <div className="relative flex gap-5 pb-8 last:pb-0">
                    {i < MILESTONES.length - 1 && (
                      <span className="absolute left-[27px] top-12 bottom-0 w-px bg-line" aria-hidden="true" />
                    )}
                    <span className="relative z-10 flex items-center justify-center w-9 h-9 rounded-full bg-navy text-teal-bright text-[10px] font-extrabold tracking-wide shrink-0">
                      {m.year === "Today" ? "→" : m.year.slice(2)}
                    </span>
                    <div>
                      <p className="font-display font-extrabold text-navy">
                        <span className="text-teal-dark mr-2">{m.year}</span>
                        <span className="text-[15px]">{m.title}</span>
                      </p>
                      <p className="mt-1 text-sm leading-relaxed text-slate">{m.copy}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.15}>
              <Link
                to="/about"
                data-testid="history-cta"
                className="group mt-8 inline-flex items-center gap-2 bg-navy text-white text-sm font-bold px-6 py-3.5 rounded-full transition-all duration-300 hover:bg-teal hover:text-navy"
              >
                Get to Know Us
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>

          <div className="relative h-[420px] sm:h-[520px]">
            <motion.div style={reduced ? {} : { y: yMain }} className="absolute right-0 top-0 w-[78%] rounded-3xl overflow-hidden shadow-lift border border-white">
              <img
                src={IMG_MAIN}
                alt="Business owner reviewing finances at a desk"
                loading="lazy"
                className="w-full h-[340px] sm:h-[420px] object-cover"
              />
            </motion.div>
            <motion.div style={reduced ? {} : { y: ySecond }} className="absolute left-0 bottom-0 w-[52%] rounded-2xl overflow-hidden shadow-lift border-4 border-white">
              <img
                src={IMG_SECONDARY}
                alt="Small business owner taking a call at his shop doorway"
                loading="lazy"
                className="w-full h-[220px] sm:h-[260px] object-cover"
              />
            </motion.div>
            <div className="absolute left-2 top-6 z-10 bg-navy text-white rounded-2xl px-5 py-4 shadow-lift rotate-[-3deg]">
              <p className="font-display font-extrabold text-2xl text-teal-bright">Since 2001</p>
              <p className="text-[11px] font-semibold uppercase tracking-wider2 text-ice/70 mt-0.5">Community Promotions</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
